import { useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import { useConversationStore } from '@/stores/conversationStore';
import type { FeedbackAction } from '@/lib/types';
import { toast } from 'sonner';

interface SubmitFeedbackParams {
  action: FeedbackAction;
  feedbackText?: string;
  currentDraftText?: string;
  regenerationReason?: 'tone' | 'length' | 'accuracy' | 'other';
}

export function useFeedback() {
  const draft = useConversationStore((state) => state.draft);
  const currentConversation = useConversationStore((state) => state.currentConversation);
  const originalDraftText = useConversationStore((state) => state.originalDraftText);
  const draftGeneratedAt = useConversationStore((state) => state.draftGeneratedAt);
  const setFeedbackSubmitted = useConversationStore((state) => state.setFeedbackSubmitted);

  return useMutation({
    mutationFn: async (params: SubmitFeedbackParams) => {
      if (!draft?.id) {
        console.error('Draft ID is missing, cannot submit feedback');
        throw new Error('Draft ID is required for feedback submission');
      }

      if (!currentConversation?.id) {
        console.error('Conversation ID is missing, cannot submit feedback');
        throw new Error('Conversation ID is required for feedback submission');
      }

      // For regeneration feedback, we don't need all tracking data
      const isRegenerationFeedback = params.action === 'regenerated';

      if (!isRegenerationFeedback && (!originalDraftText || !draftGeneratedAt)) {
        console.error('Draft tracking data is missing:', {
          hasOriginalText: !!originalDraftText,
          hasDraftGeneratedAt: !!draftGeneratedAt,
        });
        throw new Error('Draft tracking data is missing');
      }

      // Calculate time to decision (in seconds, not milliseconds)
      const timeToDecisionSeconds = draftGeneratedAt
        ? Math.round((Date.now() - draftGeneratedAt.getTime()) / 1000)
        : 0; // Fallback for regeneration

      // Get final text (edited or original)
      const finalText = params.currentDraftText || draft.text;

      // Submit feedback to API (backend calculates edit distance)
      await apiClient.submitFeedback({
        draft_id: draft.id,
        conversation_id: currentConversation.id,
        action: params.action,
        final_text: params.action === 'edited_before_sending' ? finalText : undefined,
        feedback_text: params.feedbackText,
        time_to_decision: timeToDecisionSeconds,
        regeneration_reason: params.regenerationReason,
      });
    },
    onSuccess: (_, params) => {
      setFeedbackSubmitted(true);

      // Show appropriate toast message
      const messages = {
        sent_as_is: 'Thanks! Feedback recorded - draft sent as-is',
        edited_before_sending: 'Thanks! Feedback recorded - draft was edited',
        rejected: 'Thanks! Feedback recorded - draft was rejected',
        regenerated: 'Thanks! Feedback recorded - draft was regenerated',
      };

      toast.success(messages[params.action] || 'Thanks! Feedback recorded');
    },
    onError: (error) => {
      console.error('Failed to submit feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    },
  });
}
